// apps/roleforge-app/src/pages/AuditInvitationsPage.tsx
// Plan 3b-ii Phase 8.5: Stakeholder invitation management page for a given audit.

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { AuditExperienceContext } from '../components/audit/AuditExperienceShell';
import { StakeholderInviteForm } from '../components/invitations/StakeholderInviteForm';
import { StakeholderInviteList } from '../components/invitations/StakeholderInviteList';
import { CapExceededModal } from '../components/cap/CapExceededModal';
import {
  listInvitations,
  deleteInvitation,
  resendInvitation,
  type InvitationRow,
} from '../lib/api-client';
import { parseCapError, type CapError } from '../lib/cap-error';
import { useAuth } from '../lib/auth';
import {
  describeInviteSlots,
  type StakeholderInviteSlots,
} from '../lib/roleforge-invite-limits';

export function AuditInvitationsPage() {
  const { audit } = useOutletContext<AuditExperienceContext>();
  const { jwt } = useAuth();

  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [capError, setCapError] = useState<CapError | null>(null);
  /** Issue #1304 — one-time invite links for invitations created in this visit. */
  const [respondUrls, setRespondUrls] = useState<Record<string, string>>({});
  // Issue #1324: the org-wide slot truth comes from the server. This page used
  // to compute `used = invitations.length` for THIS audit while the server
  // enforced a lifetime org counter.
  const [slots, setSlots] = useState<StakeholderInviteSlots | null>(null);
  /** Issue #1374 — the invitation whose resend is in flight, if any. */
  const [resendingId, setResendingId] = useState<string | null>(null);
  /** Issue #1374 — per-row resend failure text, as the server worded it. */
  const [resendErrors, setResendErrors] = useState<Record<string, string>>({});

  const loadInvitations = useCallback(async () => {
    if (!jwt) return;
    try {
      const res = await listInvitations(jwt, audit.id);
      setInvitations(res.invitations);
      setSlots(res.slots ?? null);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  }, [jwt, audit.id]);

  useEffect(() => {
    void loadInvitations();
  }, [loadInvitations]);

  const handleCreated = useCallback(
    (invitation: InvitationRow, respondUrl?: string) => {
      setInvitations((prev) => [...prev, invitation]);
      // Issue #1304: the invite link is only ever returned by the create call.
      // Hold it in page state so the owner can copy the one they just sent; a
      // reload legitimately loses it, because the server keeps only the digest.
      if (respondUrl) {
        setRespondUrls((prev) => ({ ...prev, [invitation.id]: respondUrl }));
      }
      // A send consumes an org-wide slot, so re-read the authoritative count
      // rather than decrementing a local guess.
      void loadInvitations();
    },
    [loadInvitations],
  );

  /**
   * Issue #1374 — Resend. The endpoint rotates the token and returns the fresh
   * `respond_url`, which goes into the same `respondUrls` map the create path
   * fills, so a rotated link is surfaced identically to a newly-created one.
   *
   * Failure is reported with the server's own message. Issue #1361 records
   * `TaskDetailPanel` swallowing errors in `catch { // ignore }`; a Resend that
   * quietly did nothing would leave the owner believing a broken link had been
   * replaced, so nothing here is swallowed. A cap/entitlement answer additionally
   * opens the shared modal via `parseCapError` rather than a parallel copy map.
   */
  const handleResend = useCallback(
    async (inviteId: string) => {
      if (!jwt) return;
      setResendingId(inviteId);
      setResendErrors((prev) => {
        if (!(inviteId in prev)) return prev;
        const next = { ...prev };
        delete next[inviteId];
        return next;
      });
      try {
        const res = await resendInvitation(jwt, audit.id, inviteId);
        if (res.respond_url) {
          setRespondUrls((prev) => ({ ...prev, [inviteId]: res.respond_url as string }));
        }
        // The rotation re-dated `invited_at`, so re-read rather than guess.
        await loadInvitations();
      } catch (err) {
        const cap = parseCapError(err);
        if (cap) setCapError(cap);
        setResendErrors((prev) => ({
          ...prev,
          [inviteId]:
            cap?.message ??
            (err instanceof Error && err.message ? err.message : 'Unknown error'),
        }));
      } finally {
        setResendingId(null);
      }
    },
    [jwt, audit.id, loadInvitations],
  );

  const handleDelete = useCallback(
    async (inviteId: string) => {
      if (!jwt) return;
      try {
        await deleteInvitation(jwt, audit.id, inviteId);
        setInvitations((prev) => prev.filter((inv) => inv.id !== inviteId));
      } catch (err) {
        const cap = parseCapError(err);
        if (cap) {
          setCapError(cap);
        }
      }
    },
    [jwt, audit.id],
  );

  const slotDisplay = describeInviteSlots(slots);

  return (
    <div className="dark-surface -mx-6 -my-6 min-h-[calc(100vh-5rem)] bg-surface px-6 py-7 text-ink sm:px-8">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <div className="flex flex-col gap-4 border-b border-line pb-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-coral">
              Stakeholder interviews
            </p>
            <h2 className="text-2xl font-semibold text-ink">Invite teammates into the audit</h2>
            <p className="max-w-2xl text-sm leading-6 text-graphite">
              Send async interview links to the people who know the work. Their responses are
              attached to this audit before the AI team is finalized.
            </p>
          </div>
          <div
            className="rounded-md border border-line bg-card px-3 py-2 text-sm text-graphite"
            data-testid="invite-slot-count"
          >
            <span className="font-semibold text-ink">{slotDisplay.usage}</span>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <section className="rounded-lg border border-line bg-card p-5 shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-base font-semibold text-ink">Invite a stakeholder</h3>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-graphite">
                  The interview link opens a focused response flow. No VibeControl account setup is
                  required for the invited person.
                </p>
              </div>
              <span
                data-testid="invite-slots-remaining"
                className="inline-flex w-fit items-center rounded-md bg-mint/10 px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-mint"
              >
                {slotDisplay.remaining}
              </span>
            </div>
            <StakeholderInviteForm auditId={audit.id} onCreated={handleCreated} />
          </section>

          <aside className="rounded-lg border border-line bg-card p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-graphite">
              Invite limit
            </p>
            <p className="mt-3 text-3xl font-semibold text-ink" data-testid="invite-slot-cap">
              {slots?.unlimited ? '∞' : (slots?.cap ?? '—')}
            </p>
            <p className="mt-2 text-sm leading-6 text-graphite" data-testid="invite-slot-note">
              {slotDisplay.note}
            </p>
          </aside>
        </div>

        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-graphite">
            Sent invitations
          </h3>
          {loading ? (
            <p className="text-sm text-graphite">Loading...</p>
          ) : loadError ? (
            <p className="rounded-md border border-ember/40 bg-ember/10 px-3 py-2 text-sm text-ember">
              {loadError}
            </p>
          ) : (
            <StakeholderInviteList
              invitations={invitations}
              respondUrls={respondUrls}
              onDelete={handleDelete}
              onResend={handleResend}
              resendingId={resendingId}
              resendErrors={resendErrors}
            />
          )}
        </section>
      </div>

      {capError && (
        <CapExceededModal error={capError} onClose={() => setCapError(null)} />
      )}
    </div>
  );
}
